import { CircleCheck, CircleDot, TriangleAlert } from 'lucide-react';
import type { MetaDto, SnapshotMetaDto } from '@crm/shared/contracts';
import { formatTime } from '../lib/format.ts';
import { useT } from '../lib/i18n.tsx';
import { cn } from '../lib/cn.ts';
import { InfoPopover } from './InfoPopover.tsx';

type Freshness = (MetaDto | SnapshotMetaDto)['dataFreshness'];

const STYLE = {
  fresh: { Icon: CircleCheck, className: 'text-success' },
  delayed: { Icon: CircleDot, className: 'text-warning' },
  stale: { Icon: TriangleAlert, className: 'text-destructive' },
} as const;

/**
 * "Data as of 18:58 ● fresh": always visible. The status is a word and an icon as well as a colour.
 * Demo data is fresh by construction, which the popover explains.
 */
export function FreshnessIndicator({ freshness }: { freshness: Freshness | undefined }) {
  const t = useT();
  if (!freshness)
    return <span className="h-5 w-44 animate-pulse rounded bg-muted" aria-hidden="true" />;
  const { Icon, className } = STYLE[freshness.status];
  const time = freshness.lastSyncedAt ? formatTime(freshness.lastSyncedAt) : null;
  return (
    <div className="flex items-center gap-1 text-sm" data-testid="freshness">
      <span>{time ? t.freshness.asOf(time) : t.freshness.neverSynced}</span>
      <span className={cn('inline-flex items-center gap-1 font-medium', className)}>
        <Icon aria-hidden="true" className="size-4" />
        {t.freshness[freshness.status]}
      </span>
      {freshness.evaluatedAs === 'demo' ? (
        <InfoPopover label={t.freshness.label}>
          <p>{t.freshness.demoNote}</p>
        </InfoPopover>
      ) : null}
    </div>
  );
}
